import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react-native';
import { useQueryClient } from '@tanstack/react-query';
import {
    ActivityIndicator,
    FlatList,
    NativeScrollEvent,
    NativeSyntheticEvent,
    ScrollView,
    StyleSheet,
    Text,
    View,
    useWindowDimensions,
} from 'react-native';
import { Stack, useLocalSearchParams, usePathname } from 'expo-router';
import { FontAwesome6 } from '@expo/vector-icons';
import { interRegular, interSemiBold } from '../../lib/fonts';
import Header from '../Header';
import MetaTags from '../MetaTags';
import colors from '../../lib/colors';
import Footer from '../Footer';
import Banner from '../ui/Banner';
import {
    FEED_REQUEST_TIMEOUT_MS,
    FeedPaper,
    ProjectFeedSortOption,
    useProjectFeedCount,
    useProjectFeedInfinite,
} from '../../lib/hooks/useProjectFeed';
import { useBillingPreviewDigest } from '../../lib/hooks/useBillingPreviewDigest';
import { useProjectFeedSearch } from '../../lib/hooks/useProjectFeedSearch';
import { useProjectPaperFeedback } from '../../lib/hooks/useProjectPaperFeedback';
import { useProject } from '../../lib/ProjectContext';
import { isRequestTimeoutError } from '../../lib/asyncTimeout';
import { isNetworkError } from '../../lib/networkErrorUtils';
import { useCopyAtomFeedUrl } from '../../lib/hooks/useCopyAtomFeedUrl';
import { useCurrentEntitlements } from '../../lib/hooks/useCurrentEntitlements';
import {
    buildRssUpgradeMessage,
    canAccessRssDelivery,
    getRssUpgradeAction,
} from '../../lib/entitlements';
import {
    isProjectFeedFeedbackFilter,
    DownvoteReason,
    FeedbackVote,
    matchesFeedbackFilter,
    ProjectFeedFeedbackFilter,
} from '../../lib/projectPaperFeedback';
import ProjectFeedDetailPane from '../feed/ProjectFeedDetailPane';
import ProjectFeedDetailSheet from '../feed/ProjectFeedDetailSheet';
import ProjectFeedListItem from '../feed/ProjectFeedListItem';
import ProjectFeedListControls from '../feed/ProjectFeedListControls';
import ProjectFeedToolbar from '../feed/ProjectFeedToolbar';
import { getFeedPaperHref } from '../feed/projectFeedUi';
import { buildCanonicalDashboardPath } from '../../lib/dashboardRoutes';
import { getPaperAuthorNames } from '../../lib/paperAuthors';
import {
    buildBillingPreviewTrialHref,
    classifyBillingPreviewProjectAccess,
    getBillingPreviewState,
} from '../../lib/billingPreviewState';
import PreviewTrackingCard from '../billing/PreviewTrackingCard';
import PreviewHiddenMatchesNotice from '../billing/PreviewHiddenMatchesNotice';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import type { LocalizedMessage } from '../../lib/i18n/messages';

const DESKTOP_BREAKPOINT = 1024;
const SEARCH_DEBOUNCE_MS = 500;

function matchesSearchQuery(item: FeedPaper, searchQuery: string) {
    if (!searchQuery.trim()) {
        return true;
    }

    const query = searchQuery.toLowerCase();
    if (item.paper.title.toLowerCase().includes(query)) return true;
    if (getPaperAuthorNames(item.paper.authors).some((author) => author.toLowerCase().includes(query))) return true;
    if (item.paper.abstract?.toLowerCase().includes(query)) return true;
    if (item.summary?.toLowerCase().includes(query)) return true;
    if (item.paper_title?.toLowerCase().includes(query)) return true;
    return false;
}

function matchesFeedView(
    item: FeedPaper,
    feedbackFilter: ProjectFeedFeedbackFilter,
    searchQuery: string
) {
    return matchesFeedbackFilter(item.project_paper_feedback, feedbackFilter)
        && matchesSearchQuery(item, searchQuery);
}

function withFeedback(
    item: FeedPaper,
    vote: FeedbackVote | null,
    reason: DownvoteReason = null
): FeedPaper {
    if (!vote) {
        return {
            ...item,
            project_paper_feedback: null,
        };
    }

    return {
        ...item,
        project_paper_feedback: {
            vote,
            downvote_reason: vote === 'downvote' ? reason : null,
            updated_at: new Date().toISOString(),
        },
    };
}

export default function ProjectFeedScreen() {
    const { i18n } = useLingui();
    const previousProcessingPhaseRef = useRef<string | null>(null);
    const reportedFeedErrorRef = useRef<string | null>(null);
    const previewPauseRefreshRef = useRef<string | null>(null);
    const { digestId, feedback } = useLocalSearchParams<{
        digestId: string;
        feedback?: string;
    }>();
    const pathname = usePathname();
    const { width } = useWindowDimensions();
    const { project, isLoading: isProjectLoading } = useProject();
    const queryClient = useQueryClient();
    const entitlementsQuery = useCurrentEntitlements();
    const [previewRefreshError, setPreviewRefreshError] = useState<LocalizedMessage | null>(null);
    const [verifiedPausedAccessKey, setVerifiedPausedAccessKey] = useState<string | null>(null);
    const pausedAt = (project as any)?.paused_at as string | null | undefined;
    const pausedAccessKey = digestId && pausedAt ? `${digestId}:${pausedAt}` : null;
    const billingPreviewState = getBillingPreviewState(entitlementsQuery.data);
    const serverPreviewAccess = classifyBillingPreviewProjectAccess(
        entitlementsQuery.data,
        digestId,
        {
            isPending: entitlementsQuery.isPending,
            isChecking: entitlementsQuery.isFetching,
            isError: entitlementsQuery.isError,
        }
    );
    const pausedAccessNeedsVerification = Boolean(
        pausedAccessKey && verifiedPausedAccessKey !== pausedAccessKey
    );
    const previewAccess = serverPreviewAccess === 'restricted'
        ? 'restricted'
        : pausedAccessNeedsVerification
            ? previewRefreshError
                ? 'unavailable'
                : 'checking'
            : serverPreviewAccess;
    const isPreviewFeed = previewAccess === 'restricted';
    const previewAccessRetryMessage = previewRefreshError
        ?? (isPreviewFeed && entitlementsQuery.isError
            ? msg({
                id: 'projectFeed.previewRefreshFailed',
                message: 'Paperzilla could not refresh the latest preview status. The feed remains limited until access is verified.',
            })
            : null);
    const isEntitlementClassificationPending = previewAccess === 'checking';
    const isEntitlementClassificationBlocked = previewAccess === 'checking'
        || previewAccess === 'unavailable';
    const fullFeedEnabled = !isProjectLoading
        && Boolean(project)
        && previewAccess === 'unrestricted';
    const isRssEntitlementPending = isEntitlementClassificationPending;
    const canCopyAtomFeedUrl = !isPreviewFeed && !isEntitlementClassificationBlocked && (
        entitlementsQuery.isError
        || (!isRssEntitlementPending && canAccessRssDelivery(entitlementsQuery.data))
    );
    const rssBlockedMessage = previewAccess === 'unavailable'
        ? msg({ id: 'projectFeed.rss.accessUnavailable', message: 'Paperzilla could not verify this project\'s access. Please reload and try again.' })
        : isRssEntitlementPending
        ? msg({ id: 'projectFeed.rss.accessChecking', message: 'Checking your plan. Please try again in a moment.' })
        : isPreviewFeed && billingPreviewState.isAwaitingDigest
        ? msg({ id: 'projectFeed.rss.previewPreparing', message: 'Your one-time preview is still being prepared. RSS becomes available when ongoing tracking starts.' })
        : buildRssUpgradeMessage(entitlementsQuery.data);
    const previewTrialHref = buildBillingPreviewTrialHref({
        projectId: billingPreviewState.projectId,
        resultDigestId: billingPreviewState.resultDigestId,
    });
    const previewDigestPending = isPreviewFeed && billingPreviewState.isAwaitingDigest;
    const rssUpgradeAction = getRssUpgradeAction(entitlementsQuery.data);
    const previewTrackingState = billingPreviewState.isPaused
        ? 'paused' as const
        : previewDigestPending
            ? 'preparing' as const
            : 'pausing' as const;
    const {
        copyAtomFeedUrl,
        isCopying,
        copied,
        error: atomFeedError,
        notice: atomFeedNotice,
    } = useCopyAtomFeedUrl({
        canCopy: canCopyAtomFeedUrl,
        blockedMessage: rssBlockedMessage,
    });
    const [feedbackFilter, setFeedbackFilter] = useState<ProjectFeedFeedbackFilter>(
        isProjectFeedFeedbackFilter(feedback) ? feedback : 'all'
    );
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [sortOption, setSortOption] = useState<ProjectFeedSortOption>('date');
    const [selectedProjectPaperId, setSelectedProjectPaperId] = useState<string | null>(null);
    const [selectedPaperSnapshot, setSelectedPaperSnapshot] = useState<FeedPaper | null>(null);
    const [isDetailSheetOpen, setIsDetailSheetOpen] = useState(false);

    const {
        data: infiniteData,
        isLoading: isInfiniteLoading,
        error: infiniteError,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch: refetchFeedPages,
    } = useProjectFeedInfinite(digestId, 'all', feedbackFilter, sortOption, {
        enabled: fullFeedEnabled,
    });
    const {
        data: previewDigest,
        isLoading: isPreviewLoading,
        error: previewError,
    } = useBillingPreviewDigest(billingPreviewState.resultDigestId, {
        enabled: isPreviewFeed && Boolean(billingPreviewState.resultDigestId),
    });
    const previewPapers = previewDigest?.papers ?? [];
    const previewTotalCount = previewDigest?.totalCount ?? previewPapers.length;
    const entitlementClassificationError = previewAccess === 'unavailable'
        ? new Error('Paperzilla could not verify this project\'s preview access. Please reload the page and try again.')
        : null;
    const error = entitlementClassificationError
        ?? (isPreviewFeed ? previewError : infiniteError);

    const {
        data: regularFeedTotal = 0,
        refetch: refetchFeedTotal,
    } = useProjectFeedCount(digestId, 'all', 'all', {
        enabled: fullFeedEnabled,
    });
    const {
        data: searchData,
        fetchNextPage: fetchNextSearchPage,
        hasNextPage: hasNextSearchPage,
        isFetching: isFetchingSearch,
        isFetchingNextPage: isFetchingNextSearchPage,
        error: searchError,
    } = useProjectFeedSearch(digestId, debouncedSearchQuery, feedbackFilter, null, {
        enabled: fullFeedEnabled,
    });
    const {
        setFeedback,
        clearFeedback,
        isPending: isFeedbackPending,
        errorMessage: feedbackErrorMessage,
        clearError: clearFeedbackError,
    } = useProjectPaperFeedback({ projectId: digestId, surface: 'feed' });
    const processingPhase = (project?.processing_phase as string | null) ?? null;
    const isFeedTimeoutError = isRequestTimeoutError(error);
    const feedErrorMessage = isFeedTimeoutError
        ? i18n._(msg({ id: 'projectFeed.loadTimeout', message: 'Something got stuck while loading your paper feed. Please reload the page and try again. If it keeps happening, email hello@paperzilla.ai and we will take a look.' }))
        : i18n._(msg({ id: 'projectFeed.loadFailure', message: 'Please reload the page and try again. If it keeps happening, email hello@paperzilla.ai.' }));

    const isDesktop = width >= DESKTOP_BREAKPOINT;
    const isCompactToolbar = width <= 1080;
    const horizontalPadding = isDesktop ? 32 : 24;
    const desktopListPaneWidth = Math.min(
        Math.max((width - horizontalPadding * 2 - 24) * 0.47, 440),
        760
    );
    const projectName = project?.name || 'Project';
    const hasActiveFeedFilter = !isPreviewFeed && feedbackFilter !== 'all';
    const displayedTotalCount = isPreviewFeed
        ? previewTotalCount
        : regularFeedTotal;
    const trimmedSearchQuery = searchQuery.trim();
    const showSearchAllPapersHint = trimmedSearchQuery.length > 0 && trimmedSearchQuery.length < 3;
    const isSearchMode = trimmedSearchQuery.length >= 3;
    const isRemoteSearchEnabled = debouncedSearchQuery.length >= 3;

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearchQuery(trimmedSearchQuery);
        }, SEARCH_DEBOUNCE_MS);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [trimmedSearchQuery]);

    useEffect(() => {
        if (!digestId) {
            previousProcessingPhaseRef.current = null;
            return;
        }

        const previousPhase = previousProcessingPhaseRef.current;
        if (previousPhase && previousPhase !== processingPhase) {
            const transitionedToCompleted = previousPhase !== 'completed' && processingPhase === 'completed';
            if (transitionedToCompleted && fullFeedEnabled) {
                void refetchFeedPages();
                void refetchFeedTotal();
            }
        }

        previousProcessingPhaseRef.current = processingPhase;
    }, [digestId, fullFeedEnabled, processingPhase, refetchFeedPages, refetchFeedTotal]);

    const refreshPreviewAccess = useCallback(async () => {
        setPreviewRefreshError(null);
        try {
            await entitlementsQuery.refreshAuthoritatively();
            if (pausedAccessKey) {
                setVerifiedPausedAccessKey(pausedAccessKey);
            }
        } catch {
            setPreviewRefreshError(
                msg({
                    id: 'projectFeed.finalPreviewRefreshFailed',
                    message: 'Tracking changed, but Paperzilla could not refresh the final preview status. The feed remains limited until access is verified.',
                })
            );
        }
    }, [entitlementsQuery.refreshAuthoritatively, pausedAccessKey]);

    useEffect(() => {
        if (!pausedAccessKey) {
            previewPauseRefreshRef.current = null;
            return;
        }

        if (previewPauseRefreshRef.current === pausedAccessKey) {
            return;
        }

        previewPauseRefreshRef.current = pausedAccessKey;
        void refreshPreviewAccess();
    }, [pausedAccessKey, refreshPreviewAccess]);

    useEffect(() => {
        if (!digestId || previewAccess === 'unrestricted') {
            return;
        }

        setSelectedProjectPaperId(null);
        setSelectedPaperSnapshot(null);
        setIsDetailSheetOpen(false);
        void Promise.all([
            queryClient.cancelQueries({ queryKey: ['project-feed-infinite', digestId] }),
            queryClient.cancelQueries({ queryKey: ['project-feed-count', digestId] }),
            queryClient.cancelQueries({ queryKey: ['project-feed-search', digestId] }),
        ]);
    }, [digestId, previewAccess, queryClient]);

    useEffect(() => {
        if (!error) {
            reportedFeedErrorRef.current = null;
            return;
        }

        const errorKey = `${digestId}:${error.name}:${error.message}`;
        if (reportedFeedErrorRef.current === errorKey) {
            return;
        }

        reportedFeedErrorRef.current = errorKey;

        const isTimeoutError = isRequestTimeoutError(error);
        if (!isNetworkError(error) || isTimeoutError) {
            Sentry.captureException(error, {
                tags: {
                    surface: 'project_feed',
                    error_kind: isTimeoutError ? 'timeout' : 'load_error',
                },
                contexts: {
                    project_feed: {
                        project_id: digestId,
                        feedback_filter: feedbackFilter,
                        sort_option: sortOption,
                        route: pathname,
                        timeout_ms: FEED_REQUEST_TIMEOUT_MS,
                    },
                },
            });
        }
    }, [digestId, error, feedbackFilter, pathname, sortOption]);

    const allPapers = useMemo(
        () => isPreviewFeed
            ? previewPapers
            : infiniteData?.pages.flatMap((page) => page.papers) ?? [],
        [infiniteData, isPreviewFeed, previewPapers]
    );
    const isWorkspaceLoading = isEntitlementClassificationPending
        || isProjectLoading
        || (isPreviewFeed ? isPreviewLoading : isInfiniteLoading);

    const filteredPapers = useMemo(() => {
        if (isPreviewFeed) {
            return allPapers;
        }

        const feedbackFilteredPapers = allPapers.filter((item) => (
            matchesFeedbackFilter(item.project_paper_feedback, feedbackFilter)
        ));

        return feedbackFilteredPapers.filter((item) => matchesSearchQuery(item, searchQuery));
    }, [allPapers, feedbackFilter, isPreviewFeed, searchQuery]);

    const searchPapers = useMemo(
        () => searchData?.pages.flatMap((page) => page.papers) ?? [],
        [searchData]
    );
    const isShowingRemoteSearchResults = isRemoteSearchEnabled
        && debouncedSearchQuery === trimmedSearchQuery
        && Boolean(searchData);
    const isSearchPending = isSearchMode && (
        debouncedSearchQuery !== trimmedSearchQuery
        || (isRemoteSearchEnabled && isFetchingSearch && !isFetchingNextSearchPage)
    );
    const visiblePapers = isPreviewFeed
        ? allPapers
        : isShowingRemoteSearchResults
            ? searchPapers
            : filteredPapers;
    const previewCountMessage = i18n._({
        ...msg({
            id: 'projectFeed.previewCount',
            message: 'We found {foundCount, plural, one {# paper} other {# papers}}. {showTop, select, yes {Showing the top {visibleCount}.} other {Showing {visibleCount}.}}',
        }),
        values: {
            foundCount: displayedTotalCount,
            showTop: displayedTotalCount > visiblePapers.length ? 'yes' : 'no',
            visibleCount: visiblePapers.length,
        },
    });
    const visiblePaperIds = useMemo(
        () => new Set(visiblePapers.map((item) => item.id)),
        [visiblePapers]
    );
    const hasMoreVisiblePapers = isShowingRemoteSearchResults
        ? Boolean(hasNextSearchPage)
        : Boolean(hasNextPage);
    const isFetchingMoreVisiblePapers = isShowingRemoteSearchResults
        ? isFetchingNextSearchPage
        : isFetchingNextPage;

    const selectedPaper = useMemo(() => {
        if (!selectedProjectPaperId) {
            return null;
        }

        if (isPreviewFeed) {
            return visiblePapers.find((item) => item.id === selectedProjectPaperId) ?? null;
        }

        return visiblePapers.find((item) => item.id === selectedProjectPaperId)
            ?? allPapers.find((item) => item.id === selectedProjectPaperId)
            ?? (selectedPaperSnapshot?.id === selectedProjectPaperId ? selectedPaperSnapshot : null);
    }, [allPapers, isPreviewFeed, selectedPaperSnapshot, selectedProjectPaperId, visiblePapers]);

    const selectedPaperHref = useMemo(() => {
        if (!selectedPaper || !digestId) {
            return undefined;
        }

        return getFeedPaperHref(selectedPaper, digestId, pathname);
    }, [digestId, pathname, selectedPaper]);

    const clearSelectedPaper = useCallback(() => {
        setSelectedProjectPaperId(null);
        setSelectedPaperSnapshot(null);
        setIsDetailSheetOpen(false);
    }, []);

    useEffect(() => {
        if (!selectedProjectPaperId) {
            setSelectedPaperSnapshot(null);
            return;
        }

        const nextSelectedPaper = visiblePapers.find((item) => item.id === selectedProjectPaperId)
            ?? allPapers.find((item) => item.id === selectedProjectPaperId);
        if (nextSelectedPaper) {
            setSelectedPaperSnapshot(nextSelectedPaper);
        }
    }, [allPapers, selectedProjectPaperId, visiblePapers]);

    useEffect(() => {
        if ((!isShowingRemoteSearchResults && !isPreviewFeed) || !selectedProjectPaperId) {
            return;
        }

        if (!visiblePaperIds.has(selectedProjectPaperId)) {
            clearSelectedPaper();
        }
    }, [clearSelectedPaper, isPreviewFeed, isShowingRemoteSearchResults, selectedProjectPaperId, visiblePaperIds]);

    useEffect(() => {
        if (isDesktop) {
            setIsDetailSheetOpen(false);
        }
    }, [isDesktop]);

    const handleSetSelectedFeedback = useCallback(async (
        vote: FeedbackVote,
        reason: DownvoteReason = null
    ) => {
        if (!selectedPaper) {
            return;
        }

        const previousFeedback = selectedPaper.project_paper_feedback;
        setSelectedPaperSnapshot((current) => (
            current?.id === selectedPaper.id ? withFeedback(current, vote, reason) : current
        ));

        try {
            await setFeedback(selectedPaper.id, vote, reason, previousFeedback);
        } catch {
            setSelectedPaperSnapshot((current) => (
                current?.id === selectedPaper.id
                    ? { ...current, project_paper_feedback: previousFeedback }
                    : current
            ));
        }
    }, [selectedPaper, setFeedback]);

    const handleClearSelectedFeedback = useCallback(async () => {
        if (!selectedPaper) {
            return;
        }

        const previousFeedback = selectedPaper.project_paper_feedback;
        setSelectedPaperSnapshot((current) => (
            current?.id === selectedPaper.id ? withFeedback(current, null) : current
        ));

        try {
            await clearFeedback(selectedPaper.id, previousFeedback);
        } catch {
            setSelectedPaperSnapshot((current) => (
                current?.id === selectedPaper.id
                    ? { ...current, project_paper_feedback: previousFeedback }
                    : current
            ));
        }
    }, [clearFeedback, selectedPaper]);

    const handleEndReached = useCallback(() => {
        if (isPreviewFeed) {
            return;
        }

        if (isShowingRemoteSearchResults) {
            if (hasNextSearchPage && !isFetchingNextSearchPage) {
                fetchNextSearchPage();
            }
            return;
        }

        if (isSearchMode) {
            return;
        }

        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [
        fetchNextPage,
        fetchNextSearchPage,
        hasNextPage,
        hasNextSearchPage,
        isFetchingNextPage,
        isFetchingNextSearchPage,
        isPreviewFeed,
        isSearchMode,
        isShowingRemoteSearchResults,
    ]);

    const handleSelectPaper = useCallback((paperId: string) => {
        const nextSelectedPaper = visiblePapers.find((item) => item.id === paperId)
            ?? allPapers.find((item) => item.id === paperId)
            ?? null;
        setSelectedProjectPaperId(paperId);
        setSelectedPaperSnapshot(nextSelectedPaper);

        if (!isDesktop) {
            setIsDetailSheetOpen(true);
        }
    }, [allPapers, isDesktop, visiblePapers]);

    const handleCloseSheet = useCallback(() => {
        clearSelectedPaper();
    }, [clearSelectedPaper]);

    const handleSearchQueryChange = useCallback((value: string) => {
        setSearchQuery(value);

        if (selectedPaper && !matchesFeedView(selectedPaper, feedbackFilter, value)) {
            clearSelectedPaper();
        }
    }, [clearSelectedPaper, feedbackFilter, selectedPaper]);

    const handleFeedbackFilterChange = useCallback((nextFilter: ProjectFeedFeedbackFilter) => {
        setFeedbackFilter(nextFilter);

        if (selectedPaper && !matchesFeedView(selectedPaper, nextFilter, searchQuery)) {
            clearSelectedPaper();
        }
    }, [clearSelectedPaper, searchQuery, selectedPaper]);

    const handleCopyAtomFeedUrl = useCallback(async () => {
        if (!digestId) {
            return;
        }

        await copyAtomFeedUrl(digestId);
    }, [copyAtomFeedUrl, digestId]);

    const renderFeedRow = useCallback((item: FeedPaper) => (
        <ProjectFeedListItem
            item={item}
            selected={selectedProjectPaperId === item.id}
            onPress={() => handleSelectPaper(item.id)}
            testID={`project-feed-row-${item.id}`}
        />
    ), [handleSelectPaper, selectedProjectPaperId]);

    const handleMobilePageScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
        const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
        const distanceFromBottom = contentSize.height - (contentOffset.y + layoutMeasurement.height);

        if (distanceFromBottom < 360) {
            handleEndReached();
        }
    }, [handleEndReached]);

    const renderCenteredState = (title: string, body: string, icon: 'rss' | 'magnifying-glass') => (
        <View style={styles.centerState}>
            <FontAwesome6 name={icon} size={28} color={colors.text500} />
            <Text style={[styles.centerStateTitle, interSemiBold]}>
                {title}
            </Text>
            <Text style={[styles.centerStateBody, interRegular]}>
                {body}
            </Text>
        </View>
    );

    const renderListEmptyState = () => (
        <View style={styles.listEmptyState} testID="project-feed-list-empty-state">
            {isSearchPending || previewDigestPending ? (
                <ActivityIndicator size="small" color={colors.primaryGreen} />
            ) : (
                <FontAwesome6 name="magnifying-glass" size={18} color={colors.text500} />
            )}
            <Text style={[styles.listEmptyTitle, interSemiBold]}>
                {previewDigestPending
                    ? <Trans id="digest.projectfeedscreen.preparingYourOneTimePreview" comment="Conditional UI copy in components/digest/ProjectFeedScreen.tsx">Preparing your one-time preview</Trans>
                    : isSearchPending
                    ? <Trans id="digest.projectfeedscreen.searchingAllPapers" comment="Conditional UI copy in components/digest/ProjectFeedScreen.tsx">Searching all papers...</Trans>
                    : isSearchMode && isRemoteSearchEnabled && searchError
                        ? <Trans id="digest.projectfeedscreen.searchUnavailable" comment="Conditional UI copy in components/digest/ProjectFeedScreen.tsx">Search unavailable</Trans>
                        : <Trans id="digest.projectfeedscreen.noResultsFound" comment="Conditional UI copy in components/digest/ProjectFeedScreen.tsx">No results found</Trans>}
            </Text>
            <Text style={[styles.listEmptyBody, interRegular]}>
                {previewDigestPending
                    ? <Trans id="digest.projectfeedscreen.yourStrongestMatchesStayLimitedWhileThePreviewEmailIs" comment="Conditional UI copy in components/digest/ProjectFeedScreen.tsx">Your strongest matches stay limited while the preview email is being prepared. Tracking will pause after delivery.</Trans>
                    : isSearchPending
                    ? <Trans id="digest.projectfeedscreen.lookingThroughTheFullFeedForMatches" comment="Conditional UI copy in components/digest/ProjectFeedScreen.tsx">Looking through the full feed for matches.</Trans>
                    : isSearchMode && isRemoteSearchEnabled && searchError
                        ? <Trans id="projectFeed.searchFailure">Unable to search all papers right now. Please try again.</Trans>
                        : <Trans id="digest.projectfeedscreen.tryADifferentSearchTermOrClearAFilter" comment="Conditional UI copy in components/digest/ProjectFeedScreen.tsx">Try a different search term or clear a filter.</Trans>}
            </Text>
        </View>
    );

    const hasAnyProjectPapers = displayedTotalCount > 0 || allPapers.length > 0;
    const hasAnyPaperRows = visiblePapers.length > 0;
    const shouldShowProjectEmptyState = !hasAnyProjectPapers && !selectedPaper && !trimmedSearchQuery;
    const shouldRenderDetailSheet = !isWorkspaceLoading
        && !error
        && Boolean(selectedPaper && selectedPaperHref);

    const renderDesktopStateCard = (content: React.ReactNode) => (
        <View style={styles.desktopWorkspace}>
            <View style={styles.desktopStateCard}>
                {content}
            </View>
        </View>
    );

    const renderMobileList = () => (
        <View style={styles.mobileListPane} testID="project-feed-list-pane">
            {isPreviewFeed ? (
                <View style={styles.previewCount} testID="project-feed-preview-count">
                    <Text style={[styles.previewCountTitle, interSemiBold]}><Trans id="digest.projectfeedscreen.oneTimePreview" comment="UI copy in components/digest/ProjectFeedScreen.tsx">
                        One-time preview
                    </Trans></Text>
                    <Text style={[styles.previewCountBody, interRegular]}>
                        {previewDigestPending && displayedTotalCount === 0
                            ? <Trans id="digest.projectfeedscreen.weArePreparingTheStrongestMatchesForYourOneTimePreview" comment="Conditional UI copy in components/digest/ProjectFeedScreen.tsx">We are preparing the strongest matches for your one-time preview.</Trans>
                            : previewCountMessage}
                    </Text>
                </View>
            ) : <ProjectFeedListControls
                shownCount={visiblePapers.length}
                totalCount={displayedTotalCount}
                searchQuery={searchQuery}
                feedbackFilter={feedbackFilter}
                sortOption={sortOption}
                isSearchMode={isSearchMode}
                hasMoreSearchResults={isShowingRemoteSearchResults && hasMoreVisiblePapers}
                isSearchPending={isSearchPending}
                showSearchAllPapersHint={showSearchAllPapersHint}
                onSearchQueryChange={handleSearchQueryChange}
                onFeedbackFilterChange={handleFeedbackFilterChange}
                onSortOptionChange={setSortOption}
            />}

            {hasAnyPaperRows ? (
                <View style={styles.listContent}>
                    {visiblePapers.map((item, index) => (
                        <View key={item.id}>
                            {renderFeedRow(item)}
                            {index < visiblePapers.length - 1 ? (
                                <View style={styles.listSeparator} />
                            ) : null}
                        </View>
                    ))}

                    {isPreviewFeed && !previewDigestPending ? (
                        <PreviewHiddenMatchesNotice
                            totalFoundCount={displayedTotalCount}
                            shownCount={previewPapers.length}
                            previewProjectId={billingPreviewState.projectId}
                            resultDigestId={billingPreviewState.resultDigestId}
                            style={styles.previewHiddenMatchesNoticeMobile}
                        />
                    ) : null}

                    {isFetchingMoreVisiblePapers ? (
                        <View style={styles.listFooter}>
                            <ActivityIndicator size="small" color={colors.primaryGreen} />
                        </View>
                    ) : null}
                </View>
            ) : (
                <View style={styles.listEmptyShell}>
                    {renderListEmptyState()}
                </View>
            )}
        </View>
    );

    const renderDesktopListPane = () => (
        <View
            style={[styles.desktopListPane, { width: desktopListPaneWidth }]}
            testID="project-feed-list-pane"
        >
            {isPreviewFeed ? (
                <View style={styles.previewCount} testID="project-feed-preview-count">
                    <Text style={[styles.previewCountTitle, interSemiBold]}><Trans id="digest.projectfeedscreen.oneTimePreview" comment="UI copy in components/digest/ProjectFeedScreen.tsx">
                        One-time preview
                    </Trans></Text>
                    <Text style={[styles.previewCountBody, interRegular]}>
                        {previewDigestPending && displayedTotalCount === 0
                            ? <Trans id="digest.projectfeedscreen.weArePreparingTheStrongestMatchesForYourOneTimePreview" comment="Conditional UI copy in components/digest/ProjectFeedScreen.tsx">We are preparing the strongest matches for your one-time preview.</Trans>
                            : previewCountMessage}
                    </Text>
                </View>
            ) : <ProjectFeedListControls
                shownCount={visiblePapers.length}
                totalCount={displayedTotalCount}
                searchQuery={searchQuery}
                feedbackFilter={feedbackFilter}
                sortOption={sortOption}
                isSearchMode={isSearchMode}
                hasMoreSearchResults={isShowingRemoteSearchResults && hasMoreVisiblePapers}
                isSearchPending={isSearchPending}
                showSearchAllPapersHint={showSearchAllPapersHint}
                onSearchQueryChange={handleSearchQueryChange}
                onFeedbackFilterChange={handleFeedbackFilterChange}
                onSortOptionChange={setSortOption}
            />}

            {hasAnyPaperRows ? (
                <FlatList
                    data={visiblePapers}
                    renderItem={({ item }) => renderFeedRow(item)}
                    keyExtractor={(item) => item.id}
                    ItemSeparatorComponent={() => <View style={styles.listSeparator} />}
                    contentContainerStyle={styles.desktopListScrollContent}
                    style={styles.desktopListScroll}
                    showsVerticalScrollIndicator={false}
                    onEndReached={handleEndReached}
                    onEndReachedThreshold={0.45}
                    removeClippedSubviews={false}
                    ListFooterComponent={isPreviewFeed && !previewDigestPending ? (
                        <View style={styles.previewHiddenMatchesNoticeFooter}>
                            <PreviewHiddenMatchesNotice
                                totalFoundCount={displayedTotalCount}
                                shownCount={previewPapers.length}
                                previewProjectId={billingPreviewState.projectId}
                                resultDigestId={billingPreviewState.resultDigestId}
                            />
                        </View>
                    ) : undefined}
                    testID="project-feed-list-scroll"
                />
            ) : (
                <View style={styles.desktopListEmptyPane}>
                    <View style={styles.listEmptyShell}>
                        {renderListEmptyState()}
                    </View>
                </View>
            )}
        </View>
    );

    return (
        <>
            <MetaTags
                title={`${projectName} - Paper Feed - Paperzilla`}
                description={`Browse all evaluated papers for ${projectName}.`}
                url={`https://paperzilla.ai${buildCanonicalDashboardPath(`/${digestId}`)}`}
            />
            <Stack.Screen options={{ headerShown: false }} />
            <View style={styles.page}>
                <Header hidePreviewTrialBanner={isPreviewFeed} />

                {isDesktop ? (
                    <View style={[styles.desktopBody, { paddingHorizontal: horizontalPadding }]}>
                        <View style={styles.desktopChrome}>
                            <ProjectFeedToolbar
                                digestId={digestId}
                                projectName={projectName}
                                copied={copied}
                                isCopying={isCopying}
                                atomFeedError={atomFeedError}
                                atomFeedNotice={canCopyAtomFeedUrl ? null : atomFeedNotice}
                                atomFeedUpgradeAction={rssUpgradeAction}
                                atomFeedTrialHref={isPreviewFeed ? previewTrialHref : undefined}
                                compact={isCompactToolbar}
                                onCopyAtomFeedUrl={handleCopyAtomFeedUrl}
                            />

                            {isPreviewFeed ? (
                                <PreviewTrackingCard
                                    projectName={projectName}
                                    previewProjectId={billingPreviewState.projectId}
                                    resultDigestId={billingPreviewState.resultDigestId}
                                    trackingState={previewTrackingState}
                                />
                            ) : null}

                            {previewAccessRetryMessage ? (
                                <Banner
                                    type="warning"
                                    variant="subtle"
                                    title={msg({ id: "digest.projectfeedscreen.previewStatusNeedsAnotherCheck2", message: "Preview status needs another check", comment: "UI title in components/digest/ProjectFeedScreen.tsx" })}
                                    message={previewAccessRetryMessage}
                                    action={{
                                        label: msg({ id: 'common.retry', message: 'Retry' }),
                                        onPress: () => void refreshPreviewAccess(),
                                    }}
                                />
                            ) : null}

                            {feedbackErrorMessage ? (
                                <Banner
                                    type="error"
                                    variant="subtle"
                                    title={msg({ id: "digest.projectfeedscreen.couldNotSaveFeedback2", message: "Could not save feedback", comment: "UI title in components/digest/ProjectFeedScreen.tsx" })}
                                    message={feedbackErrorMessage}
                                    dismissable
                                    onDismiss={clearFeedbackError}
                                    style={styles.feedbackBanner}
                                />
                            ) : null}
                        </View>

                        {isWorkspaceLoading ? (
                            renderDesktopStateCard(
                                <View style={styles.stateArea}>
                                    <ActivityIndicator size="large" color={colors.primaryGreen} />
                                    <Text style={[styles.loadingText, interRegular]}><Trans id="digest.projectfeedscreen.loadingPapers" comment="UI copy in components/digest/ProjectFeedScreen.tsx">
                                        Loading papers...
                                    </Trans></Text>
                                </View>
                            )
                        ) : error ? (
                            renderDesktopStateCard(
                                <View style={styles.stateArea}>
                                    <Text style={[styles.errorTitle, interSemiBold]}><Trans id="digest.projectfeedscreen.errorLoadingPapers" comment="UI copy in components/digest/ProjectFeedScreen.tsx">
                                        Error loading papers
                                    </Trans></Text>
                                    <Text style={[styles.errorBody, interRegular]}>
                                        {feedErrorMessage}
                                    </Text>
                                </View>
                            )
                        ) : shouldShowProjectEmptyState ? (
                            renderDesktopStateCard(
                                renderCenteredState(
                                    previewDigestPending ? 'Preparing your one-time preview' : 'No papers yet',
                                    previewDigestPending
                                        ? 'Your strongest matches stay limited while the preview email is being prepared. Tracking will pause after delivery.'
                                        : hasActiveFeedFilter
                                        ? 'No papers match this filter yet.'
                                        : 'Papers will appear here as they are evaluated.',
                                    'rss'
                                )
                            )
                        ) : (
                            <View style={styles.desktopWorkspace}>
                                <View style={styles.splitView} testID="project-feed-split-view">
                                    {renderDesktopListPane()}

                                    <View style={styles.desktopDetailPane}>
                                        <ProjectFeedDetailPane
                                            item={selectedPaper}
                                            paperLinkHref={selectedPaperHref}
                                            feedbackPending={selectedPaper ? isFeedbackPending(selectedPaper.id) : false}
                                            onSetFeedback={handleSetSelectedFeedback}
                                            onClearFeedback={handleClearSelectedFeedback}
                                        />
                                    </View>
                                </View>
                            </View>
                        )}
                    </View>
                ) : (
                    <>
                        <ScrollView
                            style={styles.pageScroll}
                            contentContainerStyle={[styles.pageScrollContent, { paddingHorizontal: horizontalPadding }]}
                            showsVerticalScrollIndicator={false}
                            onScroll={handleMobilePageScroll}
                            scrollEventThrottle={16}
                        >
                            <View style={styles.pageContent}>
                                <ProjectFeedToolbar
                                    digestId={digestId}
                                    projectName={projectName}
                                    copied={copied}
                                    isCopying={isCopying}
                                    atomFeedError={atomFeedError}
                                    atomFeedNotice={canCopyAtomFeedUrl ? null : atomFeedNotice}
                                    atomFeedUpgradeAction={rssUpgradeAction}
                                    atomFeedTrialHref={isPreviewFeed ? previewTrialHref : undefined}
                                    compact={true}
                                    onCopyAtomFeedUrl={handleCopyAtomFeedUrl}
                                />

                                {isPreviewFeed ? (
                                    <PreviewTrackingCard
                                        projectName={projectName}
                                        previewProjectId={billingPreviewState.projectId}
                                        resultDigestId={billingPreviewState.resultDigestId}
                                        trackingState={previewTrackingState}
                                    />
                                ) : null}

                                {previewAccessRetryMessage ? (
                                    <Banner
                                        type="warning"
                                        variant="subtle"
                                        title={msg({ id: "digest.projectfeedscreen.previewStatusNeedsAnotherCheck", message: "Preview status needs another check", comment: "UI title in components/digest/ProjectFeedScreen.tsx" })}
                                        message={previewAccessRetryMessage}
                                        action={{
                                            label: msg({ id: 'common.retry', message: 'Retry' }),
                                            onPress: () => void refreshPreviewAccess(),
                                        }}
                                    />
                                ) : null}

                                {feedbackErrorMessage ? (
                                    <Banner
                                        type="error"
                                        variant="subtle"
                                        title={msg({ id: "digest.projectfeedscreen.couldNotSaveFeedback", message: "Could not save feedback", comment: "UI title in components/digest/ProjectFeedScreen.tsx" })}
                                        message={feedbackErrorMessage}
                                        dismissable
                                        onDismiss={clearFeedbackError}
                                        style={styles.feedbackBanner}
                                    />
                                ) : null}

                                {isWorkspaceLoading ? (
                                    <View style={styles.mobileStateArea}>
                                        <ActivityIndicator size="large" color={colors.primaryGreen} />
                                        <Text style={[styles.loadingText, interRegular]}><Trans id="digest.projectfeedscreen.loadingPapers" comment="UI copy in components/digest/ProjectFeedScreen.tsx">
                                            Loading papers...
                                        </Trans></Text>
                                    </View>
                                ) : error ? (
                                    <View style={styles.mobileStateArea}>
                                        <Text style={[styles.errorTitle, interSemiBold]}><Trans id="digest.projectfeedscreen.errorLoadingPapers" comment="UI copy in components/digest/ProjectFeedScreen.tsx">
                                            Error loading papers
                                        </Trans></Text>
                                        <Text style={[styles.errorBody, interRegular]}>
                                            {feedErrorMessage}
                                        </Text>
                                    </View>
                                ) : shouldShowProjectEmptyState ? (
                                    <View style={styles.mobileStateArea}>
                                        {renderCenteredState(
                                            previewDigestPending ? 'Preparing your one-time preview' : 'No papers yet',
                                            previewDigestPending
                                                ? 'Your strongest matches stay limited while the preview email is being prepared. Tracking will pause after delivery.'
                                                : hasActiveFeedFilter
                                                ? 'No papers match this filter yet.'
                                                : 'Papers will appear here as they are evaluated.',
                                            'rss'
                                        )}
                                    </View>
                                ) : (
                                    renderMobileList()
                                )}
                            </View>

                            <Footer />
                        </ScrollView>

                        {shouldRenderDetailSheet ? (
                            <ProjectFeedDetailSheet
                                visible={isDetailSheetOpen}
                                item={selectedPaper}
                                paperLinkHref={selectedPaperHref}
                                feedbackPending={selectedPaper ? isFeedbackPending(selectedPaper.id) : false}
                                onClose={handleCloseSheet}
                                onSetFeedback={handleSetSelectedFeedback}
                                onClearFeedback={handleClearSelectedFeedback}
                            />
                        ) : null}
                    </>
                )}
            </View>
        </>
    );
}

const styles = StyleSheet.create({
    page: {
        flex: 1,
        backgroundColor: colors.bgWeak,
    },
    desktopBody: {
        flex: 1,
        minHeight: 0,
        paddingTop: 18,
        paddingBottom: 24,
        gap: 14,
    },
    desktopChrome: {
        gap: 12,
        flexShrink: 0,
    },
    pageScroll: {
        flex: 1,
    },
    pageScrollContent: {
        paddingTop: 18,
        paddingBottom: 24,
    },
    pageContent: {
        width: '100%',
        gap: 16,
    },
    feedbackBanner: {
        marginTop: 2,
    },
    previewCount: {
        paddingHorizontal: 16,
        paddingVertical: 14,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
        backgroundColor: '#f4f9ef',
    },
    previewCountTitle: {
        color: colors.primaryGreenDark,
        fontSize: 14,
        lineHeight: 20,
        marginBottom: 2,
    },
    previewCountBody: {
        color: colors.text600,
        fontSize: 13,
        lineHeight: 19,
    },
    desktopWorkspace: {
        flex: 1,
        minHeight: 0,
    },
    desktopStateCard: {
        flex: 1,
        minHeight: 0,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 24,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: 18,
        backgroundColor: colors.card,
    },
    stateArea: {
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 0,
    },
    mobileStateArea: {
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 420,
    },
    splitView: {
        flex: 1,
        minHeight: 0,
        flexDirection: 'row',
        alignItems: 'stretch',
        gap: 24,
    },
    desktopListPane: {
        flexShrink: 0,
        minWidth: 0,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: 18,
        backgroundColor: colors.card,
        overflow: 'hidden',
    },
    desktopListScroll: {
        flex: 1,
        minHeight: 0,
    },
    desktopListScrollContent: {
        paddingBottom: 24,
    },
    desktopListEmptyPane: {
        flex: 1,
        minHeight: 0,
    },
    desktopDetailPane: {
        flex: 1,
        minWidth: 0,
    },
    mobileListPane: {
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: 18,
        backgroundColor: colors.card,
        overflow: 'hidden',
    },
    listContent: {
        padding: 14,
        paddingBottom: 28,
    },
    previewHiddenMatchesNoticeMobile: {
        marginTop: 14,
    },
    previewHiddenMatchesNoticeFooter: {
        paddingHorizontal: 14,
        paddingTop: 14,
        paddingBottom: 14,
    },
    listSeparator: {
        height: 10,
    },
    listFooter: {
        paddingTop: 18,
        minHeight: 34,
        alignItems: 'center',
        justifyContent: 'center',
    },
    listEmptyShell: {
        flex: 1,
        minHeight: 220,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 22,
        paddingVertical: 28,
    },
    listEmptyState: {
        alignItems: 'center',
        gap: 10,
        maxWidth: 280,
    },
    listEmptyTitle: {
        fontSize: 16,
        lineHeight: 22,
        color: colors.text900,
        textAlign: 'center',
    },
    listEmptyBody: {
        fontSize: 13,
        lineHeight: 20,
        color: colors.text500,
        textAlign: 'center',
    },
    loadingText: {
        marginTop: 16,
        fontSize: 15,
        color: colors.text500,
    },
    errorTitle: {
        fontSize: 18,
        color: colors.errorRed,
        marginBottom: 8,
    },
    errorBody: {
        maxWidth: 480,
        fontSize: 14,
        lineHeight: 21,
        color: colors.text500,
        textAlign: 'center',
    },
    centerState: {
        alignItems: 'center',
        gap: 12,
        paddingHorizontal: 24,
    },
    centerStateTitle: {
        fontSize: 22,
        color: colors.text900,
        textAlign: 'center',
    },
    centerStateBody: {
        maxWidth: 440,
        fontSize: 15,
        lineHeight: 24,
        color: colors.text500,
        textAlign: 'center',
    },
});
